import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { pickLocalized } from "../../../i18n/pickLocalized";
import * as crmApi from "../api/crmApi";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS, DELIVERY_KEYS } from "../contract";
import { dayKey, fmtTime } from "../format";
import { intlLocaleFor } from "../../../lib/formatMoney";
import CrmDialog from "../ui/CrmDialog";
import CrmSelect from "../ui/CrmSelect";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import Chip from "../ui/Chip";
import { LABEL, FOCUS_RING } from "../ui/classes";
import { cn } from "../../../lib/utils";
import { CRM } from "../../../constants/testIds/crm";

const DAYS_AHEAD = 60;
const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * "Book session" (spec: "Opens the existing booking flow pre-filled with this client";
 * decision 7): the client is fixed, the practitioner picks a service, a day and a start.
 * The starts are the SAME open-hours engine the public calendar uses
 * (`GET /api/admin/booking/offered-starts` → `{service, starts: [{start, held_by_you}]}`);
 * the mode is the service's own (a service is online OR in person). A start taken in the
 * meantime is refused (TIME_UNAVAILABLE) and the list is read again.
 */
export default function BookSessionModal({ client, onClose, onBooked }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [services, setServices] = useState(null);
  const [serviceId, setServiceId] = useState("");
  const [starts, setStarts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [day, setDay] = useState("");
  const [start, setStart] = useState("");
  const [busy, setBusy] = useState(false);
  const zone = browserZone();

  useEffect(() => {
    let alive = true;
    crmApi.listBookingServices()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.rows || []);
        if (alive) setServices(list.filter((s) => s.active !== false && s.pricing_mode !== "per_minute"));
      })
      .catch((e) => { if (alive) setLoadError(e); });
    return () => { alive = false; };
  }, []);

  const loadStarts = (id) => {
    setStarts(null);
    setDay("");
    setStart("");
    if (!id) return;
    crmApi.listOfferedStarts({ service_id: id, days_ahead: DAYS_AHEAD })
      .then((data) => {
        const list = (data?.starts || []).filter((s) => !s.held_by_you);
        setStarts(list);
        setDay(list.length ? dayKey(list[0].start, zone) : "");
      })
      .catch((e) => setLoadError(e));
  };

  const service = services?.find((s) => s.id === serviceId) || null;
  const days = useMemo(() => {
    const map = new Map();
    (starts || []).forEach((s) => {
      const k = dayKey(s.start, zone);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s.start);
    });
    return map;
  }, [starts, zone]);
  const dayLabel = (k) => new Intl.DateTimeFormat(intlLocaleFor(lang), { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })
    .format(new Date(`${k}T12:00:00Z`));

  const book = async () => {
    setBusy(true);
    try {
      await crmApi.createAdminBooking({ client_id: client.id, service_id: serviceId, start, notify_client: true });
      toast.success(t("book.done"));
      onBooked?.();
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
      if (apiErrorCode(e) === CRM_ERRORS.timeUnavailable) loadStarts(serviceId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" title={t("book.title")} testId={CRM.book}
      footer={(
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={book} disabled={!start || busy} data-testid={CRM.bookSubmit}>{t("book.submit")}</CrmButton>
        </>
      )}>
      <div className="space-y-4">
        <Chip variant="neutral" className="text-sm">{client.display_name || client.primary_email}</Chip>
        {loadError && <CrmLoadFailure error={loadError} />}
        <div>
          <label htmlFor="crm-book-service" className={LABEL}>{t("book.service")}</label>
          <CrmSelect id="crm-book-service" className="w-full" selectClassName="w-full" value={serviceId} placeholder={t("book.chooseService")}
            onChange={(e) => { setServiceId(e.target.value); loadStarts(e.target.value); }}
            options={(services || []).map((s) => ({ value: s.id, label: pickLocalized(s, "name", lang) || s.name || s.id }))}
            data-testid={CRM.bookService} />
        </div>
        {service && (
          <p data-testid={CRM.bookMode} className="text-sm text-eh-ink">
            {t("book.mode")}: <strong>{t(DELIVERY_KEYS[service.delivery_mode] || "enum.delivery.in_person")}</strong>
          </p>
        )}
        {starts && starts.length === 0 && <p data-testid={CRM.bookNoSlots} className="text-sm text-eh-muted">{t("book.noSlots")}</p>}
        {days.size > 0 && (
          <>
            <div>
              <label htmlFor="crm-book-day" className={LABEL}>{t("book.day")}</label>
              <CrmSelect id="crm-book-day" value={day} onChange={(e) => { setDay(e.target.value); setStart(""); }}
                options={[...days.keys()].map((k) => ({ value: k, label: dayLabel(k) }))} />
            </div>
            <div role="radiogroup" aria-label={t("book.time")} className="flex flex-wrap gap-2" data-testid={CRM.bookDay(day)}>
              {(days.get(day) || []).map((s, i) => (
                <button key={s} type="button" role="radio" aria-checked={start === s} onClick={() => setStart(s)} data-testid={CRM.bookSlot(i)} data-start={s}
                  className={cn("rounded-full px-3 py-1.5 text-sm", start === s ? "bg-eh-ink text-eh-bg" : "bg-white text-eh-ink hover:bg-eh-cream-deep", FOCUS_RING)}>
                  {fmtTime(s, lang, zone)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </CrmDialog>
  );
}
